import type { EmailCompositionValue } from '@kula-ai/components';
import type { EmailCommConfigPayload } from '@/api/useJobStageActions/useCreateJobStageAction';
import type { CreateInterviewJobStageActionCommunicationPayload } from '@/api/useJobStageActions/useCreateJobStageAction';
import type { AtsCommTemplateVariables } from '@/api/atsCommTemplates';

/** Alias for merge-variable maps used across communication panels. */
export type InterviewVariablesByCategory = AtsCommTemplateVariables;

/** Editor attachment chip shape (aligned with `@kula-ai/components` `AttachmentItem`). */
export type InterviewEditorAttachmentItem = {
  key: string;
  name: string;
  size?: number;
  mimeType?: string;
  url?: string;
  file?: File;
};

/** Side tab values for the Communication step (matches `Tabs` item values). */
export const COMMUNICATION_TAB_VALUES = [
  'candidate-communication',
  'interviewer-communication',
  'self-schedule-slot-selection-email',
  'candidate-availability-email',
] as const;

export type CommunicationTabValue = (typeof COMMUNICATION_TAB_VALUES)[number];

export const SUBJECT_MAX_LENGTH = 150;

/**
 * UI + payload token for “Organizer email” — not a user id; resolves at send time like `zoom_host_type: 'organizer'`.
 */
export const INTERVIEW_EMAIL_SENDER_ORGANIZER_VALUE = 'organizer' as const;

/**
 * Synced to `payloadRef.communication` — API fields plus `activeSubTab` and calendar-invite drafts
 * (separate from optional follow-up emails until backend exposes distinct keys).
 */
export type InterviewCommunicationPayload =
  CreateInterviewJobStageActionCommunicationPayload & {
    activeSubTab: CommunicationTabValue;
    /** Client-only drafts; mapped to `candidate_comm_template_attributes` / `reviewer_comm_template_attributes` on save. */
    calendar_invite_candidate?: EmailCommConfigPayload | null;
    calendar_invite_interviewer?: EmailCommConfigPayload | null;
    /** UI toggle for self-schedule email block (not an API field). */
    self_schedule_section_enabled?: boolean;
    /** Set in Communication `syncToPayload` for calendar-invite `name` (not sent as top-level keys). */
    _ui_calendar_candidate_name?: string;
    _ui_calendar_interviewer_name?: string;
  };

/**
 * Full client state for the Communication step (kept in `useReducer` so inactive tabs retain edits).
 */
export interface InterviewCommunicationFormState {
  activeSubTab: CommunicationTabValue;
  candidateCalendar: EmailCompositionValue;
  candidateEmailEnabled: boolean;
  candidateEmail: EmailCompositionValue;
  /** Editor attachment chips for candidate follow-up email → `candidate_email_comm_config.attachments` on save. */
  candidateEmailAttachments: InterviewEditorAttachmentItem[];
  interviewerCalendar: EmailCompositionValue;
  interviewerEmailEnabled: boolean;
  interviewerEmail: EmailCompositionValue;
  interviewerEmailAttachments: InterviewEditorAttachmentItem[];
  selfScheduleEnabled: boolean;
  selfSchedule: EmailCompositionValue;
  selfScheduleAttachments: InterviewEditorAttachmentItem[];
  availabilityEnabled: boolean;
  availability: EmailCompositionValue;
  availabilityAttachments: InterviewEditorAttachmentItem[];
  candidateIcsEnabled: boolean;
  interviewerIcsEnabled: boolean;
}

export type InterviewCommunicationAction =
  | { type: 'SET_ACTIVE_SUB_TAB'; payload: CommunicationTabValue }
  | { type: 'SET_CANDIDATE_CALENDAR'; payload: EmailCompositionValue }
  | { type: 'SET_CANDIDATE_EMAIL_ENABLED'; payload: boolean }
  | { type: 'SET_CANDIDATE_EMAIL'; payload: EmailCompositionValue }
  /** Partial merge — reducer applies `patchEmailComposition` against the
   *  LATEST state, so concurrent SET dispatches in the same commit are
   *  preserved. Use this when an effect only wants to update a few
   *  fields (e.g. apply a default template) without clobbering values
   *  set by another effect in the same render. */
  | { type: 'PATCH_CANDIDATE_EMAIL'; payload: Partial<EmailCompositionValue> }
  | {
      type: 'SET_CANDIDATE_EMAIL_ATTACHMENTS';
      payload: InterviewEditorAttachmentItem[];
    }
  | { type: 'SET_INTERVIEWER_CALENDAR'; payload: EmailCompositionValue }
  | { type: 'SET_INTERVIEWER_EMAIL_ENABLED'; payload: boolean }
  | { type: 'SET_INTERVIEWER_EMAIL'; payload: EmailCompositionValue }
  | { type: 'PATCH_INTERVIEWER_EMAIL'; payload: Partial<EmailCompositionValue> }
  | {
      type: 'SET_INTERVIEWER_EMAIL_ATTACHMENTS';
      payload: InterviewEditorAttachmentItem[];
    }
  | { type: 'SET_SELF_SCHEDULE_ENABLED'; payload: boolean }
  | { type: 'SET_SELF_SCHEDULE'; payload: EmailCompositionValue }
  | { type: 'PATCH_SELF_SCHEDULE'; payload: Partial<EmailCompositionValue> }
  | {
      type: 'SET_SELF_SCHEDULE_ATTACHMENTS';
      payload: InterviewEditorAttachmentItem[];
    }
  | { type: 'SET_AVAILABILITY_ENABLED'; payload: boolean }
  | { type: 'SET_AVAILABILITY'; payload: EmailCompositionValue }
  | { type: 'PATCH_AVAILABILITY'; payload: Partial<EmailCompositionValue> }
  | {
      type: 'SET_AVAILABILITY_ATTACHMENTS';
      payload: InterviewEditorAttachmentItem[];
    }
  | { type: 'SET_CANDIDATE_ICS_ENABLED'; payload: boolean }
  | { type: 'SET_INTERVIEWER_ICS_ENABLED'; payload: boolean }
  | { type: 'REPLACE_ALL'; payload: InterviewCommunicationFormState };
